import "./TasksDashboard.css";

import { useMemo } from "react";

import { GanttActiveFilters, GanttChart } from "../GanttChart";
import { KanbanBoard, TaskListView, TasksDashboardHeader } from "./components";
import { TasksDashboardProps } from "./models";
import { useTasksDashboardState } from "./useTasksDashboardState";
import { serverGroupsToGanttGroups, toGanttAgendaTasks } from "./utils";

/** Module-level so the chart sees the same object every render and stays controlled-empty. */
const EMPTY_GANTT_FILTERS: GanttActiveFilters = {};

const DashboardEmptyState = ({ heading, message }: { heading: string; message: string }): JSX.Element => (
  <div className="TasksDashboard_empty">
    <div className="TasksDashboard_emptyIllustration" aria-hidden>
      <div className="TasksDashboard_emptyCard">
        <span className="TasksDashboard_emptyPill TasksDashboard_emptyPill_lg" />
        <span className="TasksDashboard_emptyPill TasksDashboard_emptyPill_md" />
        <span className="TasksDashboard_emptyPill TasksDashboard_emptyPill_sm" />
        <div className="TasksDashboard_emptyDivider" />
        <span className="TasksDashboard_emptyPill TasksDashboard_emptyPill_xs" />
      </div>
    </div>
    <h1>{heading}</h1>
    <p className="TasksDashboard_emptyMessage">{message}</p>
  </div>
);

/**
 * Host for the org-wide tasks dashboard. Owns the shared header (title, view switcher, filters,
 * hide toggles and "New task") and renders whichever of the three views is selected — all three
 * read the same filtered task set.
 */
export const TasksDashboard = (props: TasksDashboardProps): JSX.Element => {
  const {
    variant = "seed",
    title = "Tasks",
    ganttGroups = [],
    listGroups = [],
    onSectionExpand,
    onSectionShowMore,
    kanbanLanes,
    onLaneShowMore,
    isLoading = false,
    onTaskClick,
    onObjectClick,
    onGroupObjectClick,
    agendaTasks,
    hasMoreAgendaTasks,
    isLoadingAgendaTasks,
    onAgendaShowMore,
    onGanttViewModeChange,
    ganttViewMode,
    onGroupExpand,
    onGroupShowMore,
    groupsHaveNextPage,
    isLoadingGroups,
    onGroupsShowMore,
    onSelectionChange,
    onNewTask,
    onExportTable,
    isExporting,
  } = props;

  const {
    isHostFiltered,
    filteredTasks,
    view,
    hideCompleted,
    hideUnassigned,
    canHideUnassigned,
    filters,
    filterOptions,
    isEmpty,
    hasNoFilterResults,
    changeView,
    toggleHideCompleted,
    toggleHideUnassigned,
    setFilter,
    applyTaskDrop,
  } = useTasksDashboardState(props);

  /*
    The type badge is always shown on Gantt groups. It was conditional on the set spanning more than one
    object type, so that a projects-only organisation saw no redundant label — but the groups are paged
    server-side, so that question can only ever be asked of the page in hand: a first page of projects
    hid the badge, and the page that brought in a deal made one appear on every row already on screen.
    An org-wide feed spans Project, Deal and Landholding by definition (BR-02824), so labelling every
    row is both stable and honest about what the list holds.
  */
  const ganttShowObjectType = true;

  /*
    The Gantt is grouped server-side: one row per parent object carrying the server's `taskCount` /
    `milestoneTaskCount` / `statusCounts`, with its rows loaded on expand. Deriving the groups from the
    loaded task set instead would give counts that only describe the cards that happen to be loaded.
  */
  const ganttChartGroups = useMemo(
    () => serverGroupsToGanttGroups(ganttGroups, { showObjectType: ganttShowObjectType }),
    [ganttGroups, ganttShowObjectType]
  );

  /*
    Agenda rows are their own query, so they are only mapped when the host supplies them — passing an
    empty array would tell the chart to render a host-driven agenda that has no rows, rather than
    letting it fall back to flattening the groups.
  */
  const ganttAgendaTasks = useMemo(() => (agendaTasks ? toGanttAgendaTasks(agendaTasks) : undefined), [agendaTasks]);

  /** The Gantt is showing the host-fed agenda, so its rows and its loading state are the agenda's. */
  const isGanttAgenda = ganttViewMode === "agenda" && agendaTasks !== undefined;

  const renderBody = (): JSX.Element => {
    if (isEmpty) {
      return (
        <DashboardEmptyState
          heading="No tasks yet"
          message="Tasks assigned across your projects and deals will appear here."
        />
      );
    }

    if (hasNoFilterResults) {
      return (
        <DashboardEmptyState
          heading="No matching tasks"
          message="No tasks match the filters you've applied. Try clearing a filter."
        />
      );
    }

    // Gantt tab: reuse the merged GanttChart widget's "tasks" variant, fed the server's group rows.
    // We omit `filterOptions` so the widget's own filter bar stays hidden and the shared dashboard
    // header remains the single source of filtering.
    if (view === "gantt") {
      return (
        <GanttChart
          variant="tasks"
          theme={variant}
          groups={ganttChartGroups}
          // The rows are their own request, so the chart is still loading while they land — otherwise
          // switching to the Gantt would flash "No tasks match the current filters" before the first
          // row arrives. Which request that is depends on the presentation: the agenda's flat list, or
          // the group headers. Reading the headers in agenda would hide loaded agenda rows behind a
          // skeleton while a request the agenda doesn't use is still in flight.
          isLoading={
            isLoading ||
            (isGanttAgenda
              ? Boolean(isLoadingAgendaTasks) && (agendaTasks ?? []).length === 0
              : Boolean(isLoadingGroups) && ganttGroups.length === 0)
          }
          // Controlled-empty, so the chart's own in-memory filtering never runs: the rows arrive
          // already filtered server-side, and a chart-level filter would also make it drop the
          // server counts (which no longer describe the rows once it filters them itself).
          activeFilters={EMPTY_GANTT_FILTERS}
          onTaskClick={onTaskClick ? (taskId) => onTaskClick(taskId) : undefined}
          // Not the dashboard's `onObjectClick`, which identifies a *task*: a group is the object, so
          // the chart reports the object's own uuid.
          onObjectClick={onGroupObjectClick}
          // Agenda spans every object, so it reads one flat paged query rather than each group's rows.
          agendaTasks={ganttAgendaTasks}
          hasMoreAgendaTasks={hasMoreAgendaTasks}
          isLoadingAgendaTasks={isLoadingAgendaTasks}
          onAgendaShowMore={onAgendaShowMore}
          onViewModeChange={onGanttViewModeChange}
          // Opens on whatever the host last heard, so the choice outlives a remount of the chart.
          defaultView={ganttViewMode}
          onGroupExpand={onGroupExpand}
          onGroupShowMore={onGroupShowMore}
          hasMoreGroups={groupsHaveNextPage}
          isLoadingMoreGroups={isLoadingGroups}
          onShowMoreGroups={onGroupsShowMore}
        />
      );
    }

    /*
      List tab: the same server group query as the Gantt, sliced into status sections. Its counts and
      rows are therefore the server's — every object is reachable through "Show more objects" and every
      section pages independently, rather than being limited to the Kanban lanes' loaded pages.
    */
    if (view === "list") {
      return (
        <TaskListView
          groups={listGroups}
          variant={variant}
          // The group headers are their own request, so the table is still loading while they land —
          // otherwise it would flash "No tasks to show." before the first group row arrives.
          isLoading={isLoading || (Boolean(isLoadingGroups) && listGroups.length === 0)}
          onTaskClick={onTaskClick}
          onExportTable={onExportTable}
          isExporting={isExporting}
          onSectionExpand={onSectionExpand}
          onSectionShowMore={onSectionShowMore}
          hasMoreGroups={groupsHaveNextPage}
          isLoadingMoreGroups={isLoadingGroups}
          onShowMoreGroups={onGroupsShowMore}
        />
      );
    }

    return (
      <KanbanBoard
        variant={variant}
        tasks={filteredTasks}
        hideCompleted={hideCompleted}
        hideUnassigned={hideUnassigned}
        // Server lane counts only apply while the host owns filtering: if the dashboard filtered the
        // cards itself, the lane totals would describe the unfiltered set and contradict the columns.
        lanes={isHostFiltered ? kanbanLanes : undefined}
        onLaneShowMore={onLaneShowMore}
        isLoading={isLoading}
        onTaskDrop={applyTaskDrop}
        onSelectionChange={onSelectionChange}
        onTaskClick={onTaskClick}
        onObjectClick={onObjectClick}
      />
    );
  };

  return (
    <div className={`TasksDashboard TasksDashboard_${variant}`}>
      <TasksDashboardHeader
        variant={variant}
        title={title}
        view={view}
        hideCompleted={hideCompleted}
        hideUnassigned={hideUnassigned}
        canHideUnassigned={canHideUnassigned}
        filters={filters}
        filterOptions={filterOptions}
        onViewChange={changeView}
        onHideCompletedChange={toggleHideCompleted}
        onHideUnassignedChange={toggleHideUnassigned}
        onFilterChange={setFilter}
        onNewTask={onNewTask}
        isLoading={isLoading}
      />
      <div className="TasksDashboard_body">{renderBody()}</div>
    </div>
  );
};
